<template>
    <widget-wrapper
    :hideRedirectIcon="!getOperatorsList.length && !applyErrorState && !showLoader"
    :extraClass="!getOperatorsList.length ? 'operators' : null"
    :variant="variant"
    :title="!getOperatorsList.length ? t('home.widgets.operators.titleCreate') : t('home.widgets.operators.titleList')"
    :applyShadowToHeader="valueScrolled"
    @redirectToFeature="!getOperatorsList.length ? navigateByName(routeList.APPROVALS_OPERATORS_CREATE_NAME) : navigateByName(routeList.APPROVALS_OPERATORS_NAME)">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- No Operators State -->
        <div v-if="!getOperatorsList.length && !applyErrorState" class="no-operators-content-wrapper" :class="{'mobile': getMobileAppState}">
            <!-- Title -->
            <div class="title">{{t('home.widgets.operators.titleEmpty')}}</div>
            <!-- Message -->
            <div class="message">{{t('home.widgets.operators.message')}}</div>
            <!-- Icon -->
            <div class="icon-button-wrapper">
                <!-- Icon -->
                <icon-operators/>
                <!-- Button -->
                <button-primary
                v-if="showCreateButton"
                icon="arrow-right-full"
                iconPosition="right"
                buttonSize="small"
                :label="t('home.widgets.operators.create')"
                @clicked="navigateByName(routeList.APPROVALS_OPERATORS_CREATE_NAME)"/>
            </div>
        </div>
        <!-- Operators List -->
        <div
        v-if="getOperatorsList.length && !applyErrorState"
        ref="scrollElementRef"
        class="operators-list-scroll-wrapper"
        :class="{ 'mobile': getMobileAppState }"
        @scroll="valueScrolled = $event.target.scrollTop">
            <table-generic
            :hasDetail="false"
            :headerData="tableHeaderData"
            :tableStyles="tableStyles"
            :tableData="tableData"
            :hasMore="getMobileAppState && getOperatorsList.length > 2 && !displayAllOperators"
			@loadMore="displayAllOperators = true"
            @moreOptionsClicked="moreOptionsClickHandler"/>
        </div>
        <div
        class="note-and-button-wrapper"
        :class="{
            'with-shadow': (!scrollIsAtEnd || valueScrolled === 0) && getOperatorsList.length,
            'mobile': getMobileAppState
        }">
            <!-- List Note -->
            <div v-if="getOperatorsList.length && !showLoader && !getMobileAppState" class="list-note">{{t('home.widgets.operators.listNote')}}</div>
            <!-- Button -->
            <button-primary
            v-if="showCreateButton"
            icon="arrow-right-full"
            iconPosition="right"
            buttonSize="small"
            :label="t('home.widgets.operators.create')"
            @clicked="navigateByName(routeList.APPROVALS_OPERATORS_CREATE_NAME)"/>
        </div>
    </widget-wrapper>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import IconOperators from '@/assets/icons/illustrations/operators.svg?inline'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import TableGeneric from '@/components/generic/table/Generic'
/* Vue & Assets */
import { ref, computed } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { iconHandler, classesHandler } from '@/assets/scripts/mappers/operators'
import { getCurrentRouteMenuItemInfo } from '@/assets/scripts/parsers/menu'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    variant: String
})

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const valueScrolled = ref(0)
const scrollElementRef = ref('')

const scrollIsAtEnd = computed(() => {
    return scrollElementRef.value.clientHeight + valueScrolled.value === scrollElementRef.value.scrollHeight
})

const showCreateButton = computed(() => {
    return getCurrentRouteMenuItemInfo(routeList.APPROVALS_OPERATORS_CREATE_PATH)
})

/* mapActions & mapGetters */
const { requestOperatorsList } = mapActions(moduleMapper.HOME)
const { getOperatorsList } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters()

const fetchOperatorsList = async () => {
    showLoader.value = true
    applyErrorState.value = false

	try {
        await requestOperatorsList()
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
        applyErrorState.value = true
	}
}
/*************************************************
*                                                *
*                   Table Datas                  *
*                                                *
*************************************************/
const displayAllOperators = ref(false) /* prop to control what we show in mobile */

const tableHeaderData = [
    {
        label: t('home.widgets.operators.name'),
        rowCellType: 'title',
        showInMobile: { groupWith: ['username', 'status'] },
        dataAttr: 'name'
    },
    {
        label: t('home.widgets.operators.acession'),
        rowCellType: 'description',
        dataAttr: 'username'
    },
    { label: t('home.widgets.operators.status'), dataAttr: 'status', showInMobile: true },
    { label: t('home.widgets.operators.condition'), dataAttr: 'condition', showInMobile: true },
    { dataAttr: 'tableOptions', showInMobile: true, rowCellType: 'more-options' }
]

const tableStyles = getMobileAppState.value ? null : [
    null,
    { width: '90px', 'max-width': '90px' },
    { width: '80px', 'max-width': '80px' },
    { width: '70px', 'max-width': '70px' },
    null
]

const tableMoreOptions = [
	{
		id: 1,
		text: t('home.widgets.operators.edit'),
		icon: 'pencil',
        routeInfo: routeList.APPROVALS_OPERATORS_EDIT_NAME
	}, {
		id: 2,
		text: t('home.widgets.operators.delete'),
		icon: 'delete',
        variant: 'negative',
        routeInfo: routeList.APPROVALS_OPERATORS_DELETE_NAME
	}
]

const moreOptionsClickHandler = row => {
	navigateByName(tableMoreOptions.find(obj => obj.id === row.optionClicked).routeInfo, { operatorId: row.rowId })
}

const tableData = computed(() => {
    const operators = getOperatorsList.value.map(operator => ([
        { value: operator.id, dataAttr: 'id' },
        { type: 'title', value: operator.name, dataAttr: 'name' },
        {
            value: {
                content: !getMobileAppState.value ? operator.username : `${t('home.widgets.operators.acession')}: ${operator.username}`,
                classes: 'capitalize'
            },
            dataAttr: 'username'
        },
        {
            type: 'description',
            value: !getMobileAppState.value
                ? operator.status?.operatorStatusDescription || '-'
                : `${t('home.widgets.operators.status')}: ${operator.status?.operatorStatusDescription}`,
            dataAttr: 'status'
        },
        {
            type: 'status',
            value: {
                icon: operator.condition ? 'clock' : iconHandler(operator.status?.operatorStatusValue),
                classes: operator.condition ? 'warning' : classesHandler(operator.status?.operatorStatusValue)
            },
            dataAttr: 'condition'
        },
        { type: 'more-options', value: tableMoreOptions, dataAttr: 'tableOptions' }
    ]))

    return displayAllOperators.value || !getMobileAppState.value ? operators.slice(0, 10) : operators.slice(0, 2)
})

fetchOperatorsList()
</script>

<style lang="scss" scoped>
.no-operators-content-wrapper {
    width: 100%;
    height: 100%;
    padding: 16px 16px 0 16px;

    display: flex;
    flex-direction: column;

    .title {
        width: 100%;
        margin-bottom: 16px;

        @include H5-Medium;
        color: var(--secondary-dark-3);
        line-height: 20px;
    }

    .message {
        width: 100%;
        margin-bottom: 24px;

        @include H7-Regular;
        color: var(--secondary-dark-2);
        line-height: 16px;
    }

    .icon-button-wrapper {
        width: 100%;
        height: 100%;

        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        svg {
            width: 152px;
            height: 152px;
        }
    }

    &.mobile {
        .icon-button-wrapper {
            svg {
                width: 112px;
                height: 112px;
            }
        }
    }
}

.operators-list-scroll-wrapper {
    width: 100%;
    height: calc(100% - 72px);
    padding: 16px;

    overflow: auto;
    @include generic-scrollbar(null, var(--primary));

    &.mobile {
        height: fit-content;
        padding: 16px 0 0 0;

        .selector-wrapper {
            width: calc(100% - 32px);
            margin: 0 auto 16px auto;

            :deep(.dropdown-wrapper) {
                width: 100%;
            }
        }
    }
}

.note-and-button-wrapper {
    width: 100%;
    padding: 24px 16px 16px 16px;

    display: flex;
    justify-content: space-between;

    &.with-shadow {
        box-shadow: var(--box-shadow-1);
    }

    .list-note {
        @include H6-Regular;
        color: var(--secondary-dark-4);
        line-height: 32px;

        transform: translateZ(0);
    }

    &.mobile {
        justify-content: flex-end;
        padding: 16px 16px 0 16px;
    }
}
</style>
